import { Primitive } from "@radix-ui/react-primitive";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../utils";
import { Error } from "../fields/Error";
import { Label } from "../Label";

const fontVariants = cva("", {
  variants: {
    font: {
      light: "font-light",
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
    },
  },
  defaultVariants: {
    font: "semibold",
  },
});

const textVariants = cva("", {
  variants: {
    size: {
      sm: ["text-xs"],
      md: ["text-sm"],
      lg: ["text-base"],
      xl: ["text-lg"],
    },
  },
  defaultVariants: {
    size: "md",
  },
});

const inputVariants = cva(
  "w-full border border-gray-200 bg-white p-2 px-4 shadow-md placeholder:text-gray-600/30 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 md:rounded lg:rounded",
  {
    variants: {
      size: {
        sm: "h-8",
        md: "h-10",
        lg: "h-12",
        xl: "h-16",
      },
      variant: {
        default: "border bg-white hover:border-primary/30",
        error: "border-red-500",
        success: "border-green-500",
      },
    },
    defaultVariants: {
      size: "md",
      variant: "default",
    },
  },
);

type InputVariants = VariantProps<typeof inputVariants> &
  VariantProps<typeof fontVariants>;

export interface InputProps
  extends Omit<React.ComponentPropsWithoutRef<typeof Primitive.input>, "size">,
    InputVariants {
  rightElement?: React.ReactNode;
  leftElement?: React.ReactNode;
  error?: string;
  label?: React.ReactNode;
}

export const Input = React.forwardRef<
  React.ElementRef<typeof Primitive.input>,
  InputProps
>(
  (
    {
      className,
      size,
      variant,
      rightElement,
      error,
      font,
      leftElement,
      label,
      ...props
    },
    ref,
  ) => (
    <>
      {label && <Label className="font-semibold">{label}</Label>}
      <div
        className={cn(
          "relative w-full text-sm text-black",
          fontVariants({ font }),
          textVariants({ size }),
          { "text-red-200": !!error },
        )}
      >
        <Primitive.input
          ref={ref}
          className={cn(
            inputVariants({ size, variant }),
            "text-base",
            "peer",
            {
              "pl-10": leftElement,
              "pr-6": rightElement,
              "border-red-200 focus-visible:border-red-200 focus-visible:ring-red-200":
                !!error,
            },
            className,
          )}
          {...props}
        />
        {leftElement && (
          <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-4 font-semibold peer-disabled:opacity-50">
            {leftElement}
          </div>
        )}
        {rightElement && (
          <div className="pointer-events-none absolute inset-y-0 end-0 flex items-center justify-center pe-4 font-semibold peer-disabled:opacity-50">
            {rightElement}
          </div>
        )}
      </div>
      {error && <Error className="ml-1 mt-1">{error}</Error>}
    </>
  ),
);
